/** Documentul `kiosk-layout.v1` (contracts/schemas/kiosk-layout.v1.schema.json), tipizat pentru editor și previzualizare. */
export type Localized = Record<string, string>;
export type Lang = 'ro' | 'en' | 'hu' | 'de';
export type Preset = 'office' | 'public' | 'hotel' | 'car' | 'service';
export type IconName =
  | 'locker'
  | 'box'
  | 'pickup'
  | 'doc'
  | 'gov'
  | 'key'
  | 'bed'
  | 'car'
  | 'wrench'
  | 'cart'
  | 'support'
  | 'shield'
  | 'clock'
  | 'qr'
  | 'phone'
  | 'id'
  | 'check'
  | 'return'
  | 'info';

export interface KioskAction {
  id: string;
  title: Localized;
  text: Localized;
  icon: IconName;
  primary: boolean;
  flow: {
    operation: 'deposit' | 'pickup';
    identification: { methods: ('numeric' | 'qr')[]; numericLength?: number };
    afterOpen: { waitDoorClose: boolean; askFinished: boolean; allowReopenOnce: boolean };
    texts: {
      success: Localized;
      afterClose: Localized;
      invalidCode: Localized;
      doorFailed: Localized;
      offline: Localized;
    };
    timeouts: { doorOpenSec: number; sessionSec: number };
  };
}

export interface KioskDoc {
  schemaVersion: number;
  languages: Lang[];
  defaultLanguage: Lang;
  theme: {
    preset?: Preset | null;
    custom?: { accent: string; accentDark: string; surfaceA: string; surfaceB: string } | null;
  };
  logoAssetId?: string | null;
  header: { product: Localized; showStatusChip: boolean };
  hero: { icon: IconName; kicker: Localized; title: Localized; subtitle: Localized };
  actions: KioskAction[];
  contextPanel: {
    visible: boolean;
    icon: IconName;
    title: Localized;
    text: Localized;
    tag: Localized;
  };
  footer: {
    note: Localized;
    help: {
      mode: 'phone' | 'instructions' | 'disabled';
      phone?: string | null;
      instructions?: Localized | null;
    };
  };
  behavior: { idleTimeoutSec: number };
}

export const PRESETS: Record<
  Preset,
  {
    accent: string;
    accent2: string;
    soft: string;
    surfaceA: string;
    surfaceB: string;
    orb: string;
    orb2: string;
    label: string;
  }
> = {
  office: {
    accent: '#1463ff',
    accent2: '#0b46b7',
    soft: '#eaf1ff',
    surfaceA: '#f7faff',
    surfaceB: '#eaf2fb',
    orb: '#1463ff',
    orb2: '#51a5ff',
    label: 'Birou / curier',
  },
  public: {
    accent: '#005cb9',
    accent2: '#003f80',
    soft: '#e7f2ff',
    surfaceA: '#f7fbff',
    surfaceB: '#e8f1f8',
    orb: '#006dcc',
    orb2: '#4ba5c9',
    label: 'Instituție publică',
  },
  hotel: {
    accent: '#137a68',
    accent2: '#0a584b',
    soft: '#e5f5f1',
    surfaceA: '#fbfdfc',
    surfaceB: '#eaf4f0',
    orb: '#28a287',
    orb2: '#e7b56a',
    label: 'Aparthotel',
  },
  car: {
    accent: '#e25c2a',
    accent2: '#aa3511',
    soft: '#fff0e9',
    surfaceA: '#fffaf7',
    surfaceB: '#f3ece6',
    orb: '#ee6b35',
    orb2: '#224d78',
    label: 'Rent-a-car',
  },
  service: {
    accent: '#7053d5',
    accent2: '#4b35a8',
    soft: '#f0ecff',
    surfaceA: '#fbfaff',
    surfaceB: '#eeeaf7',
    orb: '#8068df',
    orb2: '#2aa4a1',
    label: 'Pickup / service',
  },
};

export const LANG_LABEL: Record<Lang, string> = {
  ro: 'Română',
  en: 'Engleză',
  hu: 'Maghiară',
  de: 'Germană',
};

export function t(map: Localized | null | undefined, lang: string, fallback: string): string {
  if (!map) return '';
  return map[lang] || map[fallback] || Object.values(map).find(Boolean) || '';
}

/** Accentul la 10 % peste alb, ca în aplicația kiosk. */
export function accentSoft(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#eaf1ff';
  const n = parseInt(m[1]!, 16);
  const ch = (v: number) => Math.round(255 - (255 - v) * 0.1);
  const r = ch((n >> 16) & 255);
  const g = ch((n >> 8) & 255);
  const b = ch(n & 255);
  return `#${[r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}
